import { fuseDark } from '@fuse/colors';
import { lightBlue, red } from '@mui/material/colors';
import { createTheme, ThemeOptions, PaletteMode } from '@mui/material/styles';
import qs from 'qs';
import { FuseSettingsConfigType } from '@fuse/core/FuseSettings/FuseSettings';

/**
 * The defaultTheme object defines the default color palette for the application.
 */
const defaultTheme = {
	palette: {
		mode: 'light',
		text: {
			primary: 'rgb(17, 24, 39)',
			secondary: 'rgb(107, 114, 128)',
			disabled: 'rgb(149, 156, 169)',
		},
		common: {
			black: 'rgb(17, 24, 39)',
			white: 'rgb(255, 255, 255)',
		},
		primary: {
			light: '#bec1c5',
			main: '#252f3e',
			dark: '#0d121b',
			contrastDefaultColor: 'light',
		},
		secondary: {
			light: '#bdf2fa',
			main: '#22d3ee',
			dark: '#0cb7e2',
		},
		background: {
			paper: '#FFFFFF',
			default: '#F3F4FC',
		},
		error: {
			light: '#ffcdd2',
			main: '#f44336',
			dark: '#b71c1c',
		},
	},
};

/**
 * The defaultSettings object defines the default settings for the Fuse application.
 */
export const defaultSettings = {
	customScrollbars: true,
	direction: 'ltr',
	layout: {},
	theme: {
		main: defaultTheme,
		navbar: defaultTheme,
		toolbar: defaultTheme,
		footer: defaultTheme,
	},
};

/**
 * The getParsedQuerySettings function parses the query string to retrieve the default settings for the Fuse application.
 * It returns a FuseSettingsConfigType object that can be used to configure the application.
 */
export function getParsedQuerySettings(): FuseSettingsConfigType | object {
	if (typeof window === 'undefined') {
		return {};
	}

	const parsedQueryString = qs.parse(window?.location?.search, { ignoreQueryPrefix: true });

	const { defaultSettings = {} } = parsedQueryString;

	if (typeof defaultSettings === 'string') {
		// Handle the case when defaultSettings is a string
		return JSON.parse(defaultSettings) as FuseSettingsConfigType;
	}

	return {};
}

/**
 * The defaultThemeOptions object defines the default options for the MUI theme.
 */
export const defaultThemeOptions = {
	cssVariables: true,
	typography: {
		fontFamily: ['Inter var', 'Inter', 'Roboto', '"Helvetica"', 'Arial', 'sans-serif'].join(','),
		fontWeightLight: 300,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
	},
	breakpoints: {
		values: {
			xs: 0, // Extra small devices
			sm: 600, // Small devices
			md: 960, // Medium devices
			lg: 1280, // Large devices
			xl: 1920, // Extra large devices
		},
	},
	components: {
		MuiChip: {
			styleOverrides: {
				label: {
					fontSize: '12px',
					fontWeight: 500,
				},
				colorDefault: {
					backgroundColor: '#E4E6F6',
					color: '#131942',
				},
				colorSuccess: {
					backgroundColor: '#23842A',
					color: '#FFFFFF',
				},
				colorWarning: {
					backgroundColor: '#D89705',
					color: '#FFFFFF',
				},
				colorError: {
					backgroundColor: '#AE2222',
					color: '#FFFFFF',
				},
			},
		},
		MuiSvgIcon: {
			defaultProps: {},
			styleOverrides: {
				root: {},
				sizeSmall: {
					width: 16,
					height: 16,
				},
				sizeMedium: {
					width: 20,
					height: 20,
				},
				sizeLarge: {
					width: 24,
					height: 24,
				},
			},
		},
		MuiAppBar: {
			defaultProps: {
				enableColorOnDark: true,
			},
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
			},
		},
		MuiPickersPopper: {
			styleOverrides: {
				root: {
					zIndex: 99999,
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				popper: {
					zIndex: 99999,
				},
			},
		},
		MuiButtonBase: {
			defaultProps: {},
			styleOverrides: {
				root: {},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
				},
				sizeMedium: {
					width: 36,
					height: 36,
					maxHeight: 36,
				},
				sizeSmall: {
					width: 32,
					height: 32,
					maxHeight: 32,
				},
				sizeLarge: {
					width: 40,
					height: 40,
					maxHeight: 40,
				},
			},
		},
		MuiBadge: {
			defaultProps: {},
			styleOverrides: {
				root: {},
			},
		},
		MuiAvatar: {
			defaultProps: {},
			styleOverrides: {
				root: {
					width: 36,
					height: 36,
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: 'text',
				color: 'inherit',
			},
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: '600',
					fontSize: 16,
				},
				sizeSmall: {
					borderRadius: 8,
					minHeight: 32,
					maxHeight: 40,
					lineHeight: '150%',
					padding: '8px 22px',
				},
				sizeMedium: {
					borderRadius: 8,
					minHeight: 36,
					maxHeight: 48,
					padding: '12px 22px',
				},
				sizeLarge: {
					minHeight: 40,
					maxHeight: 60,
					borderRadius: 8,
					padding: '18px 24px',
				},
				contained: {
					boxShadow: 'none',
					'&:hover, &:focus': {
						boxShadow: 'none',
					},
					'&.Mui-disabled': {
						backgroundColor: '#F3F4FC',
						color: '#A8AAB8',
						border: 'none',
					},
				},
				outlined: {
					'&.Mui-disabled': {
						backgroundColor: 'transparent',
						color: '#A8AAB8',
						borderColor: '#DADBE1',
						borderWidth: 1,
						borderStyle: 'solid',
					},
				},
			},
		},
		MuiButtonGroup: {
			defaultProps: {
				color: 'secondary',
			},
			styleOverrides: {
				contained: {
					borderRadius: 8,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: 'none',
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius: 12,
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
				rounded: {
					borderRadius: 12,
				},
			},
		},
		MuiCard: {
			styleOverrides: {},
		},
		MuiPopover: {
			styleOverrides: {
				paper: {
					borderRadius: 8,
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				color: 'secondary',
				size: 'small',
			},
			styleOverrides: {
				root: {
					'& > .MuiFormHelperText-root': {
						marginLeft: 11,
					},
				},
			},
		},
		MuiInputLabel: {
			defaultProps: {
				color: 'primary',
			},
			styleOverrides: {
				shrink: {
					transform: 'translate(11px, -7px) scale(0.8)',
				},
				root: {
					transform: 'translate(11px, 8px) scale(1)',
					'&.Mui-focused': {},
				},
			},
		},
		MuiSelect: {
			defaultProps: {
				color: 'primary',
			},
			styleOverrides: {
				select: {
					minHeight: 0,
					'&.Mui-focused': {
						backgroundColor: '#F3F4FC',
					},
				},
				sizeSmall: {
					height: 40,
					minHeight: 40,
				},
				sizeMedium: {
					height: 56,
					minHeight: 56,
				},
			},
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					backgroundColor: '#F3F4FC',
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					marginRight: 0,
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					minHeight: 36,
					borderRadius: 8,
					lineHeight: 1,
				},
				legend: {
					fontSize: '0.75em',
				},
				input: {
					padding: '5px 11px',
				},
				adornedStart: {
					paddingLeft: `11px!important`,
				},
				sizeSmall: {
					height: 40,
					minHeight: 40,
					borderRadius: 8,
				},
				sizeMedium: {
					height: 56,
					minHeight: 56,
					borderRadius: 8,
				},
				sizeLarge: {
					height: 40,
					minHeight: 40,
					borderRadius: 8,
				},
			},
		},
		MuiOutlinedInput: {
			defaultProps: {
				color: 'primary',
			},
			styleOverrides: {
				root: {
					borderRadius: 8,
					'&.Mui-focused': {
						backgroundColor: '#F3F4FC',
						'& fieldset': {
							borderWidth: '2px !important',
						},
					},
					'&.MuiOutlinedInput-sizeSmall': {
						height: 40,
					},
					'&.MuiOutlinedInput-sizeMedium': {
						height: 56,
					},
					// MUI's own hover rule for the notched outline isn't guarded against
					// .Mui-disabled, so a disabled field's border still darkens on hover.
					'&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: 'var(--neutral-gray-disabled)',
					},
				},
				input: {
					padding: '5px 11px',
				},
			},
		},
		MuiFilledInput: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					'&:before, &:after': {
						display: 'none',
					},
				},

				input: {
					padding: '5px 11px',
				},
			},
		},
		MuiSlider: {
			defaultProps: {
				color: 'secondary',
			},
		},
		MuiCheckbox: {
			defaultProps: {
				color: 'secondary',
			},
		},
		MuiRadio: {
			defaultProps: {
				color: 'secondary',
			},
		},
		MuiSwitch: {
			defaultProps: {
				color: 'secondary',
			},
		},
		MuiTypography: {
			variants: [
				{
					props: { color: 'text.secondary' },
					style: {
						color: 'text.secondary',
					},
				},
			],
		},
	},
};

/**
 * The mustHaveThemeOptions object defines the options that must be present in the MUI theme.
 */
export const mustHaveThemeOptions = {
	typography: {
		fontSize: 13,
		body1: {
			fontSize: '0.8125rem',
		},
		body2: {
			fontSize: '0.8125rem',
		},
	},
};

/**
 * The defaultThemes object defines the default themes for the application.
 */
export const defaultThemes = {
	default: {
		palette: {
			mode: 'light' as PaletteMode,
			primary: fuseDark,
			secondary: {
				light: lightBlue[400],
				main: lightBlue[600],
				dark: lightBlue[700],
			},
			error: red,
		},
		status: {
			danger: 'orange',
		},
	},
	defaultDark: {
		palette: {
			mode: 'dark' as PaletteMode,
			primary: fuseDark,
			secondary: {
				light: lightBlue[400],
				main: lightBlue[600],
				dark: lightBlue[700],
			},
			error: red,
		},
		status: {
			danger: 'orange',
		},
	},
};

/**
 * The extendThemeWithMixins function extends the theme with mixins.
 */
export function extendThemeWithMixins(obj: ThemeOptions) {
	const theme = createTheme(obj);
	return {
		border: (width = 1) => ({
			borderWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderLeft: (width = 1) => ({
			borderLeftWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderRight: (width = 1) => ({
			borderRightWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderTop: (width = 1) => ({
			borderTopWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderBottom: (width = 1) => ({
			borderBottomWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
	};
}
